import { ATTACHMENT_VIDEO_MAX_FILE_SIZE, COMPOSER_ACCEPTED_VIDEO_TYPES } from '@common/media-layer/utils/constants'
import { formatBytes } from '@common/shared-ui-layer/types/validation.types'

import type { WelcomeTaskType, WelcomeTrackingMode } from '../types/types'

export const WELCOME_ONBOARDING_MAX_STEPS = 5

export const WELCOME_AUTO_DM_DEFAULT_MESSAGE_KEY = 'welcomeSetup.autoDm.defaultMessage'

export const WELCOME_ONBOARDING_STORAGE_PREFIX = 'communities.welcome-onboarding.visit'

export type WelcomeTaskCatalogGroup = 'auto' | 'video' | 'self'

export interface WelcomeTaskCatalogEntry {
  type: WelcomeTaskType
  trackingMode: WelcomeTrackingMode
  group: WelcomeTaskCatalogGroup
  labelKey: string
  /** Types that need Setup before they can be added */
  requiresSetup?: boolean
  /** Icon chip colors — CP design tokens (`var(--cp-*)`), same pattern as `STREAM_CHAT_STYLES`. */
  iconBg: string
  iconFg: string
}

/** Add-step modal catalog — matches Builder v4 (no Download App in v1 picker). */
export const WELCOME_TASK_CATALOG: readonly WelcomeTaskCatalogEntry[] = [
  {
    type: 'COMPLETE_PROFILE',
    trackingMode: 'AUTO',
    group: 'auto',
    labelKey: 'welcomeSetup.taskTypes.completeProfile',
    iconBg: 'var(--cp-primary-50)',
    iconFg: 'var(--cp-primary-600)',
  },
  {
    type: 'LIKE_POST',
    trackingMode: 'AUTO',
    group: 'auto',
    labelKey: 'welcomeSetup.taskTypes.likePost',
    iconBg: 'var(--cp-error-50)',
    iconFg: 'var(--cp-error-700)',
  },
  {
    type: 'FIRST_POST',
    trackingMode: 'AUTO',
    group: 'auto',
    labelKey: 'welcomeSetup.taskTypes.introPost',
    iconBg: 'var(--cp-purple-50, #F4F3FF)',
    iconFg: 'var(--cp-purple-700, #5925DC)',
  },
  {
    type: 'RSVP_EVENT',
    trackingMode: 'AUTO',
    group: 'auto',
    labelKey: 'welcomeSetup.taskTypes.rsvpEvent',
    requiresSetup: true,
    iconBg: 'var(--cp-success-50)',
    iconFg: 'var(--cp-success-700)',
  },
  {
    type: 'WATCH_VIDEO',
    trackingMode: 'SELF',
    group: 'video',
    labelKey: 'welcomeSetup.taskTypes.watchVideo',
    requiresSetup: true,
    iconBg: 'var(--cp-primary-50)',
    iconFg: 'var(--cp-primary-600)',
  },
  {
    type: 'CUSTOM_TASK',
    trackingMode: 'SELF',
    group: 'self',
    labelKey: 'welcomeSetup.taskTypes.customTask',
    requiresSetup: true,
    iconBg: 'var(--cp-gray-50, #F9FAFB)',
    iconFg: 'var(--cp-font-600)',
  },
] as const

export function getWelcomeTaskCatalogEntry(type: WelcomeTaskType): WelcomeTaskCatalogEntry | undefined {
  return WELCOME_TASK_CATALOG.find(entry => entry.type === type)
}

export function getWelcomeTrackingLabelKey(type: WelcomeTaskType, trackingMode: WelcomeTrackingMode): string {
  if (type === 'WATCH_VIDEO') {
    return 'welcomeSetup.taskTypes.videoTracked'
  }
  if (trackingMode === 'SELF') {
    return 'welcomeSetup.taskTypes.selfTracked'
  }
  return 'welcomeSetup.taskTypes.autoTracked'
}

/** Inline tokens rendered as chips in Auto DM preview / editor (BE resolveWelcomeTemplateText). */
export const WELCOME_AUTO_DM_SENDER_TOKEN = '{{SENDER_NAME}}'
export const WELCOME_AUTO_DM_MEMBER_TOKEN = '{{MEMBER_NAME}}'
/** Legacy FE tokens — still recognized when loading older saved templates. */
export const WELCOME_AUTO_DM_SENDER_TOKEN_LEGACY = '{{senderName}}'
export const WELCOME_AUTO_DM_MEMBER_TOKEN_LEGACY = '{{memberName}}'

export const WELCOME_AUTO_DM_DEFAULT_TEXT =
  "Hey, welcome to the community! 🎉\nWe're excited to have you here. Head over to discussions tab and share a bit about yourself. We'd love to get to know you."

/** Chat-parity Auto DM attachment limits (`ChatMessageEditor` defaults). */
export const WELCOME_AUTO_DM_MAX_IMAGES = 10
export const WELCOME_AUTO_DM_MAX_VIDEOS = 2
export const WELCOME_AUTO_DM_MAX_FILES = 2
/** Audio files only (not mic voice notes) — chat plus-menu Audio picker parity. */
export const WELCOME_AUTO_DM_MAX_AUDIO = 2
/** Chat video ceiling (not the 2GB DRM post limit). */
export const WELCOME_AUTO_DM_VIDEO_MAX_BYTES = 500 * 1024 * 1024
export const WELCOME_AUTO_DM_VIDEO_MAX_DURATION_SEC = 180

/** Same ceiling as posts composer video (`ATTACHMENT_VIDEO_MAX_FILE_SIZE` — DRM) for Watch Video step. */
export const WELCOME_VIDEO_MAX_BYTES = ATTACHMENT_VIDEO_MAX_FILE_SIZE

/** Same accept list as posts composer video (+ extensions for OS pickers). */
export const WELCOME_VIDEO_ACCEPT = [...COMPOSER_ACCEPTED_VIDEO_TYPES, '.mp4', '.mov', '.webm', '.avi', '.wmv'].join(',')

/** Same byte formatter as posts / media validation. */
export function formatWelcomeVideoMeta(file: File): string {
  return formatBytes(file.size, 1)
}
